import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  ChartLine, 
  ArrowsClockwise, 
  CheckCircle,
  XCircle,
  Warning,
  TrendUp,
  Lightbulb
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { Progress } from '../components/ui/progress';
import { ShareResultButton } from '../components/ShareResultButton';
import { TextHistoryPanel } from '../components/TextHistoryPanel';
import { usePersistedInput, usePersistedOutput } from '../hooks/usePersistedState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BrandAnalyzerPage = () => {
  const [websiteUrl, setWebsiteUrl] = usePersistedInput('brand-analyzer', 'websiteUrl', '');
  const [instagram, setInstagram] = usePersistedInput('brand-analyzer', 'instagram', '');
  const [tiktok, setTiktok] = usePersistedInput('brand-analyzer', 'tiktok', '');
  const [facebook, setFacebook] = usePersistedInput('brand-analyzer', 'facebook', '');
  const [linkedin, setLinkedin] = usePersistedInput('brand-analyzer', 'linkedin', '');
  const [brandDescription, setBrandDescription] = usePersistedInput('brand-analyzer', 'brandDescription', '');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = usePersistedOutput('brand-analyzer', null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('create');

  const handleHistoryReload = (item) => {
    const inputs = item.inputs || {};
    if (inputs.website_url) setWebsiteUrl(inputs.website_url);
    if (inputs.instagram) setInstagram(inputs.instagram);
    if (inputs.tiktok) setTiktok(inputs.tiktok);
    if (inputs.facebook) setFacebook(inputs.facebook);
    if (inputs.linkedin) setLinkedin(inputs.linkedin);
    if (inputs.brand_description) setBrandDescription(inputs.brand_description);
    const output = item.output || {};
    if (output.analysis) setAnalysis(output.analysis);
    setTab('create');
  };

  useEffect(() => {
    const prefill = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/user/profile`);
        const bp = data.brand_profile;
        if (bp) {
          if (bp.description) setBrandDescription(prev => prev || bp.description);
          if (bp.website) setWebsiteUrl(prev => prev || bp.website);
          if (bp.instagram) setInstagram(prev => prev || bp.instagram);
          if (bp.tiktok) setTiktok(prev => prev || bp.tiktok);
          if (bp.facebook) setFacebook(prev => prev || bp.facebook);
          if (bp.linkedin) setLinkedin(prev => prev || bp.linkedin);
        }
      } catch (e) { /* ignore */ }
    };
    prefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analyzeBrand = async () => {
    if (!brandDescription) {
      setError('Please describe your brand for the best analysis');
      return;
    }
    setError('');
    setLoading(true);
    setAnalysis(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/brand/analyze`, {
        website_url: websiteUrl,
        instagram,
        tiktok,
        facebook,
        linkedin,
        brand_description: brandDescription
      });
      
      setAnalysis(data.analysis);
    } catch (err) {
      console.error('Failed to analyze brand:', err);
      setError('Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'good': return <CheckCircle size={16} className="text-green-400" weight="fill" />;
      case 'needs_work': return <Warning size={16} className="text-yellow-400" weight="fill" />;
      case 'critical': return <XCircle size={16} className="text-red-400" weight="fill" />;
      default: return null;
    }
  };

  const getProgressColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <DashboardLayout>
      <div data-testid="brand-analyzer-page" className="space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center">
              <ChartLine size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">Brand Analyzer</h1>
              <p className="text-zinc-400 text-sm">Get an audit of your digital presence</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            {['create', 'history'].map(t => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: tab === t ? 'rgba(200,240,96,0.08)' : 'transparent',
                  border: `1px solid ${tab === t ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  color: tab === t ? '#c8f060' : 'rgba(245,240,232,0.4)',
                }}>
                {t === 'create' ? 'Analyze' : 'History'}
              </button>
            ))}
          </div>
        </motion.div>

        {tab === 'create' && !analysis ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-2xl"
          >
            <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-5">
              <h2 className="font-medium font-['Instrument_Serif',serif]">Enter Your Brand Details</h2>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Brand Description *</label>
                <textarea
                  value={brandDescription}
                  onChange={(e) => setBrandDescription(e.target.value)}
                  data-testid="brand-description-input"
                  placeholder="Describe your brand, what you sell, and your target audience..."
                  rows={4}
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Website URL</label>
                  <input
                    type="url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://yourbrand.com"
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Instagram Handle</label>
                  <input
                    type="text"
                    value={instagram}
                    onChange={(e) => setInstagram(e.target.value)}
                    placeholder="@yourbrand"
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">TikTok Handle</label>
                  <input
                    type="text"
                    value={tiktok}
                    onChange={(e) => setTiktok(e.target.value)}
                    placeholder="@yourbrand"
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Facebook Page</label>
                  <input
                    type="text"
                    value={facebook}
                    onChange={(e) => setFacebook(e.target.value)}
                    placeholder="facebook.com/yourbrand"
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">LinkedIn Page</label>
                  <input
                    type="text"
                    value={linkedin}
                    onChange={(e) => setLinkedin(e.target.value)}
                    placeholder="linkedin.com/company/yourbrand"
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                  />
                </div>
              </div>

              <button
                onClick={analyzeBrand}
                disabled={loading || !brandDescription}
                data-testid="analyze-btn"
                className="w-full bg-white text-black hover:bg-zinc-200 transition-all duration-200 rounded-full font-medium py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <ArrowsClockwise size={18} className="animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <ChartLine size={18} />
                    Analyze My Brand
                  </>
                )}
              </button>
            </div>
          </motion.div>
        ) : analysis && tab === 'create' ? (
          <div className="space-y-6">
            <p className="text-zinc-600 text-xs text-center" data-testid="ai-disclaimer">AI-assisted strategic analysis — based on your brand inputs, not real-time data</p>
            {/* Overall Score */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-8 text-center"
            >
              <h2 className="text-zinc-400 text-sm mb-2">Overall Brand Score</h2>
              <div className={`text-6xl font-bold font-['Instrument_Serif',serif] mb-2 ${getScoreColor(analysis.overall_score)}`}>
                {analysis.overall_score}
              </div>
              <p className="text-zinc-400">out of 100</p>
              <div className="mt-4">
                <ShareResultButton
                  title={`Brand Audit: ${analysis.overall_score}/100`}
                  content={`My brand scored ${analysis.overall_score}/100 on the AI Brand Analyzer. ${analysis.strengths?.[0] || ''}`}
                  type="Brand Audit"
                />
              </div>
            </motion.div>

            {/* Category Breakdown */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6"
            >
              <h2 className="font-medium font-['Instrument_Serif',serif] mb-6">Category Breakdown</h2>
              <div className="space-y-4">
                {analysis.categories?.map((category, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(category.status)}
                        <span className="text-sm font-medium">{category.name}</span>
                      </div>
                      <span className={`text-sm font-medium ${getScoreColor(category.score)}`}>{category.score}/100</span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${getProgressColor(category.score)}`}
                        style={{ width: `${category.score}%` }}
                      />
                    </div>
                    <p className="text-zinc-500 text-xs">{category.recommendation}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Strengths */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <TrendUp size={20} className="text-green-400" />
                  <h2 className="font-medium font-['Instrument_Serif',serif]">What's Working</h2>
                </div>
                <ul className="space-y-3">
                  {analysis.strengths?.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle size={16} className="text-green-400 mt-0.5" weight="fill" />
                      <span className="text-zinc-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Improvements */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb size={20} className="text-yellow-400" />
                  <h2 className="font-medium font-['Instrument_Serif',serif]">Areas to Improve</h2>
                </div>
                <ul className="space-y-3">
                  {analysis.improvements?.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Warning size={16} className="text-yellow-400 mt-0.5" weight="fill" />
                      <span className="text-zinc-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Priority Fixes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6"
            >
              <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Priority Fixes</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {analysis.priority_fixes?.map((fix, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-xl">
                    <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-medium">{index + 1}</span>
                    <span className="text-zinc-300 text-sm">{fix}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Run Again */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <button
                onClick={() => setAnalysis(null)}
                className="w-full py-2.5 text-center rounded-full border border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white text-sm transition-colors"
              >
                Run Another Analysis
              </button>
            </motion.div>
          </div>
        ) : null}

        {/* History Tab */}
        {tab === 'history' && (
          <div className="rounded-[14px] p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}>
            <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Analysis History</h2>
            <TextHistoryPanel toolType="brand_analyzer" onReload={handleHistoryReload} />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BrandAnalyzerPage;
